import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "../ui/button";
import { toast } from "sonner";
import { z } from "zod";
import { Check } from "lucide-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form } from "../ui/form";
import { useState } from "react";
import ResetPasswordDialog from "./ResetPasswordDialog";
import MyFormField from "../ui/MyFormField";
import { useUpdatePassword } from "../../lib/hooks/useUpdatePassword";

const schema = z
  .object({
    oldPassword: z.string().trim().min(1, "Requis"),
    newPassword: z
      .string()
      .trim()
      .min(8, "8 caractères minimum")
      .max(32, "32 caractères maximum")
      .regex(
        RegExp("^(?=(.*[a-z]){1,})(?=(.*[A-Z]){1,})(?=(.*[0-9]){1,}).{8,}$"),
        "Votre mot de passe doit contenir au minimum une lettre en majuscule, une lettre en minuscule et un chiffre."
      ),
    confirmNewPassword: z.string().trim(),
  })
  .superRefine((data, ctx) => {
    if (data.newPassword !== data.confirmNewPassword) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Vos mots de passe ne correspondent pas.",
        path: ["confirmNewPassword"],
      });
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Vos mots de passe ne correspondent pas.",
        path: ["newPassword"],
      });
    }
    return {};
  });
type FieldValues = z.infer<typeof schema>;

const ChangerMotDePasseDialog = () => {
  const [open, setOpen] = useState(false);
  const form = useForm<FieldValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      confirmNewPassword: "",
      newPassword: "",
      oldPassword: "",
    },
  });
  const { mutate: updatePassword, isPending } = useUpdatePassword();

  const update = (data: FieldValues) => {
    updatePassword(
      {
        oldPassword: data.oldPassword,
        newPassword: data.newPassword,
      },
      {
        onSuccess: () => {
          toast.success(`Votre mot de passe a été modifié`);
          setOpen(false);
        },
        onError: (error: any) => {
          console.error(error);
          if (error.response) {
            toast.error(error.response.data.message);
          } else {
            toast.error("Une erreur s'est produite. Veuillez réessayer ultérieurement.");
          }
        },
      }
    );
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpened) => {
        form.reset();
        setOpen(isOpened);
      }}
    >
      <DialogTrigger asChild>
        <Button variant="outline" className="w-full border-slate-300 bg-white text-slate-700 sm:w-auto">
          Modifier mon mot de passe
        </Button>
      </DialogTrigger>
      <DialogContent className="overflow-hidden p-0 sm:max-w-[440px] sm:rounded-2xl">
        <Form {...form}>
          <form onSubmit={form.handleSubmit((data) => update(data))}>
            <DialogHeader className="border-b border-slate-100 bg-[#fbfaf6] px-6 py-5 pr-12">
              <DialogTitle className="text-xl text-slate-950">Changement de mot de passe</DialogTitle>
            </DialogHeader>

            <div className="flex flex-col gap-5 px-6 py-5 [&_input]:h-11 [&_input]:border-slate-300">
              {/* Body */}
              <div>
                {/* <FormField
                  control={form.control}
                  name="oldPassword"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Mot de passe actuel</FormLabel>
                      <FormControl>
                        <Input type="password" required {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                /> */}
                <MyFormField name='oldPassword' label='Mot de passe actuel' control={form.control} type='password' />
                <div className='text-right'>
                  <ResetPasswordDialog initialEmail={""} />
                </div>
              </div>
              <MyFormField name='newPassword' label='Nouveau mot de passe' control={form.control} type='password' />
              <MyFormField
                name='confirmNewPassword'
                label='Confirmer le nouveau mot de passe'
                control={form.control}
                type='password'
              />
            </div>

            <DialogFooter className="border-t border-slate-100 bg-slate-50/70 px-6 py-4">
              <DialogClose asChild>
                <Button variant="outline">Fermer</Button>
              </DialogClose>
              <Button
                icon={<Check className="h-4 w-4" />}
                loading={isPending}
                className="bg-[#173f35] hover:bg-[#23594a]"
              >
                Valider
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default ChangerMotDePasseDialog;
